import { Injectable } from '@angular/core';
import {
  CollectionReference,
  DocumentReference,
  Firestore,
  QueryConstraint,
  WithFieldValue,
  addDoc,
  collection,
  deleteDoc,
  doc,
  query as firestoreQuery,
  getDoc,
  getDocs,
  setDoc,
} from '@angular/fire/firestore';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DbService {
  constructor(private firestore: Firestore) {}

  collection$<T>(path: string, queryFn?: QueryConstraint[]): Observable<T[]> {
    const ref = collection(this.firestore, path) as CollectionReference<T>;
    const q = queryFn ? firestoreQuery(ref, ...queryFn) : ref;

    return from(getDocs(q)).pipe(
      map((snapshot) =>
        snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(), // Handles possible undefined safely
            } as T)
        )
      )
    );
  }

  doc$<T>(path: string): Observable<T> {
    const ref = doc(this.firestore, path) as DocumentReference<T>;
    return from(getDoc(ref)).pipe(
      map((snapshot) => {
        const data = snapshot.data();
        if (!data) throw new Error(`Document at ${path} does not exist`);
        return { id: snapshot.id, ...data } as T;
      })
    );
  }

  /**
   * @param path 'collection' or 'collection/docID'
   * @param data new data
   *
   * Creates or updates data on a collection or document.
   * Returns the document ID when creating a new document.
   */
  async updateAt<T>(
    path: string,
    data: WithFieldValue<T>
  ): Promise<{ id?: string }> {
    const segments = path.split('/').filter((v) => v);
    if (segments.length % 2) {
      // Creating a new document in a collection
      const ref = collection(this.firestore, path) as CollectionReference<T>;
      const docRef = await addDoc(ref, data);
      return { id: docRef.id };
    } else {
      // Updating an existing document
      const ref = doc(this.firestore, path) as DocumentReference<T>;
      await setDoc(ref, data, { merge: true });
      return {};
    }
  }

  /**
   * @param path path to document
   *
   * Deletes document from Firestore (assuming no sub-collections)
   */
  async delete(path: string): Promise<void> {
    const ref = doc(this.firestore, path);
    await deleteDoc(ref);
  }
}
